import React, {useContext, createContext, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'

export const DISABLED_GLOBALE_DEFAULTS = {
    countryISO: 'US',
    currencyCode: 'USD',
    globaleCountry: 'US',
    globaleCurrency: 'USD'
}

export const isGlobalEEnabled = () => {
    try {
        const {
            app: {commerceAPI}
        } = getConfig()
        return !!JSON.parse(commerceAPI?.globalE?.config || '{}').enabled
    } catch {
        return false
    }
}

export const getGlobaleOrderTrackUrl = () => {
    try {
        const {
            app: {commerceAPI}
        } = getConfig()
        return JSON.parse(commerceAPI?.globalE?.config || '{}').geOrderTrackLink || ''
    } catch {
        return ''
    }
}

/**
 * Reads countryISO out of the GlobalE_Data cookie. Used before the shopper context resolves.
 * @returns {string} countryISO or ''
 */
const getGlobaleCookieCountryISO = () => {
    if (!onClient()) {
        return ''
    }
    try {
        const raw = new CookieStorage({keySuffix: ''}).get('GlobalE_Data')
        return (raw && JSON.parse(raw).countryISO) || ''
    } catch {
        return ''
    }
}

export const GlobaleContext = createContext()
export const useGlobaleContext = () => useContext(GlobaleContext) || {}

export const useResolvedCountryISO = () => {
    const {countryISO} = useGlobaleContext()

    if (!isGlobalEEnabled()) {
        return DISABLED_GLOBALE_DEFAULTS.countryISO
    }

    return countryISO || getGlobaleCookieCountryISO() || DISABLED_GLOBALE_DEFAULTS.countryISO
}

/**
 * Whether the shopper is browsing in a global-e operated country.
 * @returns {boolean}
 */
export const useIsGlobaleOperated = () => {
    const {countryISO} = useGlobaleContext()

    if (!isGlobalEEnabled()) {
        return false
    }

    const iso = countryISO || getGlobaleCookieCountryISO()

    return !!iso && iso !== 'US'
}

export const GlobaleProvider = ({children}) => {
    const value = useGlobaleProvider()

    return <GlobaleContext.Provider value={value}>{children}</GlobaleContext.Provider>
}

GlobaleProvider.propTypes = {
    children: PropTypes.node.isRequired
}

const useGlobaleProvider = () => {
    const {
        countryISO: initialCountryISO,
        currencyCode: initialCurrencyCode,
        globaleCountry: initialGlobaleCountry,
        globaleCurrency: initialGlobaleCurrency
    } = isGlobalEEnabled()
        ? {countryISO: '', currencyCode: '', globaleCountry: '', globaleCurrency: ''}
        : DISABLED_GLOBALE_DEFAULTS

    const [countryISO, setCountryISOContext] = useState(initialCountryISO)
    const [currencyCode, setCurrencyContext] = useState(initialCurrencyCode)
    const [globaleCountry, setCountryForGlobale] = useState(initialGlobaleCountry)
    const [globaleCurrency, setCurrencyForGlobale] = useState(initialGlobaleCurrency)
    const [refreshPrices, setRefreshPrices] = useState(false)
    const value = useMemo(
        () => ({
            countryISO,
            setCountryISOContext,
            setCurrencyContext,
            setCountryForGlobale,
            setCurrencyForGlobale,
            setRefreshPrices,
            globaleCountry,
            globaleCurrency,
            currencyCode,
            refreshPrices
        }),
        [countryISO, currencyCode, globaleCountry, globaleCurrency] // Always add states here
    )

    return value
}
